import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'

import Icon from './Icon'
import { UI } from '../config'
import '../styles/DropdownOpenTray.scss'

export default class DropdownOpenTray extends Component {
  static propTypes = {
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      href: PropTypes.string,
      onClick: PropTypes.func,
      isCurrent: PropTypes.bool,
      isLabelOnly: PropTypes.bool,
      target: PropTypes.string,
      rel: PropTypes.string,
    })).isRequired,
    className: PropTypes.string,
    trayTitle: PropTypes.string,
    reverseItems: PropTypes.bool,
    showTrayHeader: PropTypes.bool,
    triggerVariant: PropTypes.oneOf(['text', 'pill', 'dots']),
  }

  static defaultProps = {
    reverseItems: true,
    showTrayHeader: true,
    triggerVariant: 'text',
  }

  state = {
    open: false,
    trayMounted: false,
    trayVisible: false,
  }

  closeTimer = null

  componentDidUpdate(_, prevState) {
    const shouldLockScroll = this.state.trayMounted
    const prevShouldLockScroll = prevState.trayMounted

    if (shouldLockScroll !== prevShouldLockScroll) {
      document.body.style.overflow = shouldLockScroll ? 'hidden' : ''
    }
  }

  componentWillUnmount() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
    }
    document.body.style.overflow = ''
  }

  openTray = () => {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }

    this.setState({ open: true, trayMounted: true, trayVisible: false }, () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setState({ trayVisible: true })
        })
      })
    })
  }

  finishClose = () => {
    this.closeTimer = null
    this.setState({
      open: false,
      trayMounted: false,
      trayVisible: false,
    })
  }

  close = () => {
    if (!this.state.trayMounted) return

    this.setState({ open: false, trayVisible: false })

    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
    }

    this.closeTimer = setTimeout(this.finishClose, 300)
  }

  handleTrayTransitionEnd = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') return
    if (!this.state.trayVisible && !this.state.open) {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
      }
      this.finishClose()
    }
  }

  toggle = () => {
    if (this.state.open || this.state.trayMounted) {
      this.close()
      return
    }

    this.openTray()
  }

  handleItemActivate = (item) => {
    if (item.onClick) {
      item.onClick()
    }
    this.close()
  }

  hasNavigation() {
    return this.props.items.some(item => !item.isCurrent && !item.isLabelOnly)
  }

  isInteractive() {
    const { items } = this.props
    return items.length > 1 || this.hasNavigation()
  }

  getTrayItems() {
    const { items, reverseItems } = this.props
    return reverseItems ? [...items].reverse() : items
  }

  renderItemIndicator(item) {
    if (!item.isCurrent) return null

    return (
      <CheckCircleOutlineOutlinedIcon
        className="DropdownOpenTray-itemIcon"
        sx={{ color: '#0000008A' }}
        aria-hidden="true"
      />
    )
  }

  renderItemContent(item) {
    return (
      <>
        <span className="DropdownOpenTray-itemLabel">{item.label}</span>
        {this.renderItemIndicator(item)}
      </>
    )
  }

  renderItem(item, index) {
    const itemClassName = classNames(
      'DropdownOpenTray-item',
      item.isCurrent && 'is-current',
      item.isLabelOnly && 'is-label-only',
    )

    if (item.isCurrent || item.isLabelOnly) {
      return (
        <div
          key={index}
          className={itemClassName}
          role="menuitem"
          aria-current={item.isCurrent ? 'page' : undefined}
        >
          {this.renderItemContent(item)}
        </div>
      )
    }

    if (item.to) {
      return (
        <Link
          key={index}
          to={item.to}
          className={itemClassName}
          role="menuitem"
          onClick={() => this.handleItemActivate(item)}
        >
          {this.renderItemContent(item)}
        </Link>
      )
    }

    if (item.href) {
      return (
        <a
          key={index}
          href={item.href}
          className={itemClassName}
          role="menuitem"
          target={item.target}
          rel={item.rel}
          onClick={() => this.handleItemActivate(item)}
        >
          {this.renderItemContent(item)}
        </a>
      )
    }

    return (
      <button
        key={index}
        type="button"
        className={itemClassName}
        role="menuitem"
        onClick={() => this.handleItemActivate(item)}
      >
        {this.renderItemContent(item)}
      </button>
    )
  }

  renderTray() {
    const { trayTitle, showTrayHeader } = this.props
    const { trayMounted, trayVisible } = this.state

    if (!trayMounted) return null

    const trayItems = this.getTrayItems()
    const headerTitle = trayTitle || UI.MOBILE_BREADCRUMB_TRAY_TITLE

    return createPortal(
      <div className={classNames('DropdownOpenTray-overlay', trayVisible && 'is-visible')}>
        <button
          type="button"
          className="DropdownOpenTray-backdrop"
          onClick={this.close}
          aria-label="Close menu"
        />
        <div
          className="DropdownOpenTray-tray"
          role="menu"
          onTransitionEnd={this.handleTrayTransitionEnd}
        >
          <div className="DropdownOpenTray-handle" aria-hidden="true" />
          {showTrayHeader ? (
            <p className="DropdownOpenTray-header">{headerTitle}</p>
          ) : null}
          <div className="DropdownOpenTray-list">
            {trayItems.map((item, index) => this.renderItem(item, index))}
          </div>
        </div>
      </div>,
      document.body,
    )
  }

  renderTriggerContent() {
    const { label, triggerVariant } = this.props

    if (triggerVariant === 'dots') {
      return <Icon type="dots" className="DropdownOpenTray-dotsIcon" />
    }

    return (
      <>
        <span className="DropdownOpenTray-label">{label}</span>
        <Icon type="chevron-down" className="DropdownOpenTray-chevron" />
      </>
    )
  }

  render() {
    const { label, className, items, triggerVariant } = this.props
    const { open } = this.state

    if (!items.length) return null

    if (!this.isInteractive()) {
      return (
        <div className={classNames('DropdownOpenTray', 'DropdownOpenTray--static', className)}>
          <span className="DropdownOpenTray-label">{label}</span>
        </div>
      )
    }

    return (
      <>
        <div className={classNames(
          'DropdownOpenTray',
          triggerVariant === 'pill' && 'DropdownOpenTray--pill',
          triggerVariant === 'dots' && 'DropdownOpenTray--dots',
          className,
          open && 'is-open',
        )}>
          <button
            type="button"
            className="DropdownOpenTray-trigger"
            onClick={this.toggle}
            aria-expanded={open}
            aria-haspopup="menu"
            aria-label={triggerVariant === 'dots' ? 'Open menu' : undefined}
          >
            {this.renderTriggerContent()}
          </button>
        </div>
        {this.renderTray()}
      </>
    )
  }
}
